import anime from 'animejs';

export interface IMapController
{
    select(lat: number, lng: number, snr: string);
    unselect();
    addMarker(snr: string, name: string, lat: number, lng: number, scanCount: number, alarmCount: number);
    clearMarkers();
    setMapFeatures(darkMode: boolean, roads: boolean, poi: boolean, transit: boolean);
    setCenter(lat: number, lng: number);
    setZoom(zoom: number);
    zoomFit(onNoMarkers ? : () => void);
}

export class GoogleMapController implements IMapController
{
    private markers: any[] = [];
    private enableClustering: boolean = false;
    private selectAnimation: any = null;

    constructor(private googleMap: any)
    {
        googleMap.setOptions(
        {
            zoomControl: true,
            mapTypeControl: true,
            scaleControl: true,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
            zoomControlOptions:
            {
                position: google.maps.ControlPosition.RIGHT_CENTER,
            },
        });
    }

    public select(lat: number, lng: number, snr: string)
    {
        //Remove animation
        if (this.selectAnimation) anime.remove(`.select-${this.selectAnimation.snr}`);

        //Defer
        setTimeout(() =>
        {
            this.selectAnimation = anime(
            {

                targets: `.select-${snr}`,
                translateY: -10,
                direction: 'alternate',
                loop: true,
                easing: 'easeInOutQuad'
            });

            //Store the snr
            this.selectAnimation.snr = snr;
        }, 1000);
    }

    public unselect()
    {
        if (this.selectAnimation) anime.remove(`.select-${this.selectAnimation.snr}`);
    }

    public addMarker(snr: string, name: string, lat: number, lng: number, scanCount: number, alarmCount: number)
    {
        let marker = this.makeMarker(scanCount, alarmCount, lat, lng, name, false, 32, 'white', new google.maps.Point(16, 32), undefined, `select-${snr}`);
        this.markers.push(marker);
    }

    public clearMarkers()
    {
        for (var i = 0; i < this.markers.length; i++)
        {
            this.markers[i].setMap(null);
        }

        this.markers = [];
    }

    public setMapFeatures(darkMode: boolean, roads: boolean, poi: boolean, transit: boolean)
    {
        let styles: any = [];

        if (darkMode)
        {
            let darkModeStyles = [
            {
                elementType: 'geometry',
                stylers: [
                {
                    color: '#242f3e'
                }]
            },
            {
                elementType: 'labels.text.stroke',
                stylers: [
                {
                    color: '#242f3e'
                }]
            },
            {
                elementType: 'labels.text.fill',
                stylers: [
                {
                    color: '#746855'
                }]
            },
            {
                featureType: 'administrative.locality',
                elementType: 'labels.text.fill',
                stylers: [
                {
                    color: '#d59563'
                }]
            },
            {
                featureType: 'poi',
                elementType: 'labels.text.fill',
                stylers: [
                {
                    color: '#d59563'
                }]
            },
            {
                featureType: 'poi.park',
                elementType: 'geometry',
                stylers: [
                {
                    color: '#263c3f'
                }]
            },
            {
                featureType: 'poi.park',
                elementType: 'labels.text.fill',
                stylers: [
                {
                    color: '#6b9a76'
                }]
            },
            {
                featureType: 'road',
                elementType: 'geometry',
                stylers: [
                {
                    color: '#38414e'
                }]
            },
            {
                featureType: 'road',
                elementType: 'geometry.stroke',
                stylers: [
                {
                    color: '#212a37'
                }]
            },
            {
                featureType: 'road',
                elementType: 'labels.text.fill',
                stylers: [
                {
                    color: '#9ca5b3'
                }]
            },
            {
                featureType: 'road.highway',
                elementType: 'geometry',
                stylers: [
                {
                    color: '#746855'
                }]
            },
            {
                featureType: 'road.highway',
                elementType: 'geometry.stroke',
                stylers: [
                {
                    color: '#1f2835'
                }]
            },
            {
                featureType: 'road.highway',
                elementType: 'labels.text.fill',
                stylers: [
                {
                    color: '#f3d19c'
                }]
            },
            {
                featureType: 'transit',
                elementType: 'geometry',
                stylers: [
                {
                    color: '#2f3948'
                }]
            },
            {
                featureType: 'transit.station',
                elementType: 'labels.text.fill',
                stylers: [
                {
                    color: '#d59563'
                }]
            },
            {
                featureType: 'water',
                elementType: 'geometry',
                stylers: [
                {
                    color: '#17263c'
                }]
            },
            {
                featureType: 'water',
                elementType: 'labels.text.fill',
                stylers: [
                {
                    color: '#515c6d'
                }]
            },
            {
                featureType: 'water',
                elementType: 'labels.text.stroke',
                stylers: [
                {
                    color: '#17263c'
                }]
            }];

            styles = styles.concat(darkModeStyles);
        }

        if (!roads)
        {
            let roadStyles: any = [
            {
                featureType: "road",
                elementType: "labels",
                stylers: [
                {
                    "visibility": "off"
                }]
            },
            {
                featureType: 'road',
                elementType: 'geometry',
                stylers: [
                {
                    visibility: 'off'
                }]
            },
            {
                featureType: 'road',
                elementType: 'geometry.stroke',
                stylers: [
                {
                    visibility: 'off'
                }]
            },
            {
                featureType: 'road',
                elementType: 'labels.text.fill',
                stylers: [
                {
                    visibility: 'off'
                }]
            },
            {
                featureType: 'road.highway',
                elementType: 'geometry',
                stylers: [
                {
                    visibility: 'off'
                }]
            },
            {
                featureType: 'road.highway',
                elementType: 'geometry.stroke',
                stylers: [
                {
                    visibility: 'off'
                }]
            },
            {
                featureType: 'road.highway',
                elementType: 'labels.text.fill',
                stylers: [
                {
                    visibility: 'off'
                }]
            }];

            styles = styles.concat(roadStyles);
        }

        if (!poi)
        {
            let poiStyles = [
            {
                featureType: 'poi',
                elementType: 'labels.icon',
                stylers: [
                {
                    visibility: 'off'
                }]
            },
            {
                featureType: 'poi',
                elementType: 'labels.text.fill',
                stylers: [
                {
                    visibility: 'off'
                }]
            },
            {
                featureType: 'poi.park',
                elementType: 'geometry',
                stylers: [
                {
                    visibility: 'off'
                }]
            },
            {
                featureType: 'poi.park',
                elementType: 'labels.text.fill',
                stylers: [
                {
                    visibility: 'off'
                }]
            }];

            styles = styles.concat(poiStyles);
        }

        if (!transit)
        {
            let transitStyles = [
            {
                featureType: 'transit',
                elementType: 'geometry',
                stylers: [
                {
                    visibility: 'off'
                }]
            },
            {
                featureType: 'transit.station',
                elementType: 'labels.text.fill',
                stylers: [
                {
                    visibility: 'off'
                }]
            }];

            styles = styles.concat(transitStyles);
        }

        this.googleMap.setOptions(
        {
            styles: styles
        });
    }

    public setCenter(lat: number, lng: number)
    {
        this.googleMap.setCenter(
        {
            lat: lat,
            lng: lng
        });
    }

    public setZoom(number)
    {
        this.googleMap.setZoom(number);
    }

    public zoomFit(onNoMarkers ? : () => void)
    {
        //Markers loaded?
        if (this.markers.length > 0)
        {
            //Create an empty bounds
            var bounds = new google.maps.LatLngBounds();

            //Fill the bounds
            for (var i = 0; i < this.markers.length; i++)
            {
                bounds.extend(this.markers[i].position);
            }

            //Do a zoom fit
            this.googleMap.fitBounds(bounds);
        }
        else
        {
            if (onNoMarkers) onNoMarkers();
        }
    }

    private makeMarker(
        scanCount: number,
        alarmCount: number,
        latitude: number,
        longitude: number,
        tooltip: string | undefined,
        center: boolean = false,
        size: number = 24,
        color: string = 'red',
        anchor: google.maps.Point = new google.maps.Point(12, 24),
        image: string = 'saf-pin saf-red',
        className: string = "marker-normal")
    {
        //Get the label-marker
        const MarkerWithLabel = require('markerwithlabel')(google.maps);

        //Create the location
        let markerLatLng = {
            lat: latitude,
            lng: longitude
        };

        //Calculate the successful scans
        let goodCount = scanCount - alarmCount;
        goodCount = goodCount >= 0 ? goodCount : 0;

        //Construct
        let marker = new MarkerWithLabel(
        {
            position: markerLatLng,
            draggable: false,
            raiseOnDrag: true,
            icon: ' ',
            map: (this.enableClustering ? null : this.googleMap),
            title: tooltip,

            labelContent: `<i class="${className} saf ${image}" style="width:${size}px; height:${size}px; margin-top:10px;"></i>`,

            labelAnchor: anchor
        });

        //Set the marker to the center of the map
        if (center)
        {
            this.googleMap.setCenter(markerLatLng);
        }

        return marker;
    }
}